<template>
  <component
    :is="tag"
    v-if="mounted"
    v-bind="$attrs"
    :class="classes"
    class="preview"
    :x="x"
    :y="y"
    :transform="transform"
    :width="width"
    :height="height"
    :viewBox="viewBox"
    xmlns="http://www.w3.org/2000/svg"
    @click="onFrameClick($event)"
    @drop="emitArtworkDrop"
  >
    <!-- Mounted artwork component -->
    <RenderSvgMountedArtwork
      class="render-svg-mounted-artwork"
      :frameSpec="frameSpec"
      :x="mouldingRearWidth"
      :y="mouldingRearWidth"
      :allowUpload="allowUpload"
      :artworkPatterns="artworkPatterns"
      :loading="loading"
      :readonly="readonly"
      :inBundle="inBundle"
      @upload-click="$emit('upload-click')"
    />

    <!-- Acrylic component -->
    <RenderSvgAcrylic
      class="render-svg-acrylic"
      :frameSpec="frameSpec"
      :x="mouldingRearWidth"
      :y="mouldingRearWidth"
    />

    <!-- Moulding component -->
    <RenderSvgMoulding
      v-if="moulding"
      class="render-svg-moulding"
      :frameSpec="frameSpec"
      :moulding="moulding"
      :x="0"
      :y="0"
    />
  </component>
</template>

<script>
import { nextTick } from 'vue';
import { getMouldingInfo } from "../../framingUtilities";
import spreeClient from "../../spree";
import generateExtendedFrameSpec from '../../generateExtendedFrameSpec';
import { svgPathCalculations } from '../../generateSvgProperties';
import RenderSvgMountedArtwork from './RenderSvgMountedArtwork.vue';
import RenderSvgAcrylic from './RenderSvgAcrylic.vue';
import RenderSvgMoulding from './RenderSvgMoulding.vue';
import isEmpty from "lodash/isEmpty";
import { primaryMatColors } from "../../hardCodedValues";

export default {
  components: {
    RenderSvgMountedArtwork,
    RenderSvgAcrylic,
    RenderSvgMoulding,
  },
  props: {
    frameSpec: {
      type: Object,
      required: true
    },
    x: {
      type: Number,
      default: 0
    },
    y: {
      type: Number,
      default: 0
    },
    width: {
      type: String,
      default: '100%'
    },
    height: {
      type: String,
      default: '100%'
    },
    allowUpload: {
      type: Boolean,
      required: false,
      default: false
    },
    focusOn: {
      type: String,
      required: false,
      default: null
    },
    inBundle: {
      type: Object,
      required: false,
    },
    nestedSVG: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Array,
      default: () => []
    },
    readonly: {
      type: Boolean,
      default: false
    },
    artworkPatterns: {
      type: Array,
      required: false,
      default: () => []
    },
    needsMouldingReactivity: {
      type: Boolean,
      default: true
    }
  },
  emits: ['click', 'upload-click', 'artwork-click', 'artwork-drop', 'mounted'],
  provide () {
    return {
      emitArtworkClick: this.emitArtworkClick,
      emitArtworkDrop: this.emitArtworkDrop,
    }
  },
  data () {
    return {
      extendedFrameSpec: null,
      frame: null,
      moulding: null,
      mounted: false,
      spree: null
    }
  },
  async mounted () {
    this.spree = new spreeClient(this.$config && this.$config.joineryHost ? this.$config.joineryHost : "/");
    this.moulding = await getMouldingInfo(this.frameSpec.moulding.permalink, this.spree);

    // Conditionally add a watcher for frameSpec if needsMouldingReactivity is true
    // This avoids the high memory cost of watching frameSpec when not needed
    if (this.needsMouldingReactivity) {
      this._frameSpecUnwatch = this.$watch(
        'frameSpec',
        async function () {
          this.moulding = await getMouldingInfo(this.frameSpec.moulding.permalink, this.spree);
          if (
            !this.frameSpec.getArtwork().exterior.artworkOpeningWidthInInches ||
            !this.frameSpec.getArtwork().exterior.artworkOpeningHeightInInches
          ) {
            this.frameSpec.setArtworkOpeningDimensions();
          }
        },
        { deep: true }
      );
    }

    this.mounted = true;

    nextTick(() => {
      this.$emit('mounted');
    });
  },
  methods: {
    emitArtworkClick (artworkKey) {
      this.$emit('artwork-click', {
        frameKey: this.inBundle && this.inBundle.frameKey,
        artworkKey
      });
    },
    emitArtworkDrop (event, artworkKey) {
      this.$emit('artwork-drop', event, {
        frameKey: this.inBundle && this.inBundle.frameKey,
        artworkKey: artworkKey || 0
      });
    },
    onFrameClick (ev) {
      if (this.inBundle) {
        ev.stopPropagation();
      }

      this.emitArtworkClick(0);
    },
    async parseFrameSpec () {
      this.extendedFrameSpec = await generateExtendedFrameSpec.forFrame(this.frameSpec, 'horizontal');
      this.frame = await svgPathCalculations(
        this.extendedFrameSpec,
        this.extendedFrameSpec.offsetInBundle.leftInInches,
        this.extendedFrameSpec.offsetInBundle.topInInches,
        false
      );
    },
  },
  beforeUnmount() {
    if (this._frameSpecUnwatch) {
      this._frameSpecUnwatch();
    }
  },
  computed: {
    tag() {
      return this.nestedSVG ? 'g' : 'svg';
    },
    classes() {
      return {
        'preview--disabled': this.disabled,
        'preview--in-bundle': this.inBundle,
        'preview--readonly': this.readonly,
      };
    },
    transform() {
      return this.nestedSVG ? `translate(${this.x}, ${this.y})` : '';
    },
    artwork() {
      return this.frameSpec.getArtwork(0);
    },
    mat() {
      return this.frameSpec.mats[0] || {};
    },
    fullMat () {
      return primaryMatColors.find(mat => mat.name === this.mat.name);
    },
    isCanvasMoulding() {
      return this.frameSpec.isCanvas();
    },
    totalsOfAllMatRevealsOrMountReveals() {
      if (!isEmpty(this.mat)) {
        return {
          topInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.topInInches), 0),
          rightInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.rightInInches), 0),
          bottomInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.bottomInInches), 0),
          leftInInches: this.frameSpec.mats.reduce((acc, mat) => acc + (mat.reveal && mat.reveal.leftInInches), 0)
        };
      } else if (this.frameSpec.isFloated() || this.frameSpec.isClearFloated()) {
        return this.frameSpec.mount.reveal;
      } else {
        return {
          topInInches: 0,
          rightInInches: 0,
          bottomInInches: 0,
          leftInInches: 0
        }
      }
    },
    faceWidth() {
      // The moulding tile images for canvas mouldings include the canvas gap width in the image.
      const canvasGapWidth = this.moulding.canvas_gap_width || 0;
      return this.moulding.face_width + canvasGapWidth;
    },
    mouldingRearWidth() {
      if (this.isCanvasMoulding) {
        return this.faceWidth || 0;
      } else {
        return (this.moulding && this.moulding.rear_width) || 0;
      }
    },
    isMobile() {
      if (typeof window !== 'undefined') {
        return window.innerWidth < 832;        
      } else {
        return false;
      }
    },
    viewBox() {
      if (this.nestedSVG) return '';
      if (!this.artwork) return '0 0 0 0';
      const width = this.fullMat && this.fullMat.matWidth
        ? this.fullMat.matWidth + 2 * this.mouldingRearWidth
        : this.artwork.exterior.artworkOpeningWidthInInches + this.totalsOfAllMatRevealsOrMountReveals.leftInInches + this.totalsOfAllMatRevealsOrMountReveals.rightInInches + 2 * this.mouldingRearWidth;
      const height = this.fullMat && this.fullMat.matHeight
        ? this.fullMat.matHeight + 2 * this.mouldingRearWidth
        : this.artwork.exterior.artworkOpeningHeightInInches + this.totalsOfAllMatRevealsOrMountReveals.topInInches + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches + 2 * this.mouldingRearWidth;

      if (this.focusOn === 'moulding-plate') {
        const plateWidth = 2.36;
        const boxWidth = plateWidth + 0.5;

        let boxHeight;
        if (this.isMobile) {
          boxHeight = this.mouldingRearWidth + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches - 2;
        } else {
          boxHeight = this.mouldingRearWidth + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches + 0.5;
        }

        let boxY;
        if (this.isMobile) {
          boxY = height - this.mouldingRearWidth - this.totalsOfAllMatRevealsOrMountReveals.bottomInInches + 2.25;
        } else {
          boxY = height - this.mouldingRearWidth - this.totalsOfAllMatRevealsOrMountReveals.bottomInInches - 0.25;
        }
        
        const boxX = (width / 2) - (boxWidth / 2);

        return `${boxX} ${boxY} ${boxWidth} ${boxHeight}`;
      } else if (this.focusOn === 'mat-caption') {
        const matCaptionWidth = 2.5;
        const boxWidth = matCaptionWidth + 0.5;

        let boxHeight;
        if (this.isMobile) {
          boxHeight = this.mouldingRearWidth + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches - 1.25;
        } else {
          boxHeight = this.mouldingRearWidth + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches + 2;
        }

        let boxY;
        if (this.isMobile) {
          boxY = height - this.mouldingRearWidth - this.totalsOfAllMatRevealsOrMountReveals.bottomInInches - 0.625;
        } else {
          boxY = height - this.mouldingRearWidth - this.totalsOfAllMatRevealsOrMountReveals.bottomInInches - 1;
        }

        const matCaptionAlignment = this.mat.matCaption && this.mat.matCaption.fontAlignment;
        let boxX;

        if (matCaptionAlignment === "left") {
          boxX = this.mouldingRearWidth + this.totalsOfAllMatRevealsOrMountReveals.leftInInches - 1;
        } else if (matCaptionAlignment === "right") {
          boxX = width - this.mouldingRearWidth - this.totalsOfAllMatRevealsOrMountReveals.rightInInches - boxWidth + 1;
        } else {
          boxX = (width / 2) - (boxWidth / 2);
        }

        return `${boxX} ${boxY} ${boxWidth} ${boxHeight}`;
      } else if (this.focusOn === 'mat-plaque') {
        // needs to be changed when we introduce large plaques
        const plaqueWidth = 3.75;
        const plaqueHeight = 2.25;
        const boxWidth = plaqueWidth + 0.5;

        let boxHeight;
        if (this.isMobile) {
          boxHeight = plaqueHeight + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches - 2.8;
        } else {
          boxHeight = plaqueHeight + this.totalsOfAllMatRevealsOrMountReveals.bottomInInches + 1;
        }

        let boxY;
        if (this.isMobile) {
          boxY = height - this.mouldingRearWidth - this.totalsOfAllMatRevealsOrMountReveals.bottomInInches + 0.2;
        } else {
          boxY = height - this.mouldingRearWidth - this.totalsOfAllMatRevealsOrMountReveals.bottomInInches - 1.5;
        }

        const boxX = (width / 2) - (boxWidth / 2); 

        return `${boxX} ${boxY} ${boxWidth} ${boxHeight}`;
      } else {
        return `0 0 ${width} ${height}`;
      }
    }
  }
};
</script>

<style lang="scss">
.preview {
  max-height: 100%;
}

.preview--disabled {
  opacity: 0.25;
}

.preview--in-bundle {
  .artwork {
    cursor: pointer;
  }
}

.preview--readonly {
  pointer-events: none;
}
</style>
